import { useEffect, useRef, useState } from "react";
import { DEMO_WAIT_MAX_MS, DEMO_WAIT_POLL_INTERVAL_MS } from "./demoLanding";

type FirstLoad = { workspaceId: string; hadCandidate: boolean };
type Wait = { workspaceId: string; active: boolean; interacted: boolean };

/**
 * Keeps the first-visit demo landing working when the demo shows up after the
 * page does. While `shouldWait` holds, it refetches the catalog every
 * DEMO_WAIT_POLL_INTERVAL_MS, for at most DEMO_WAIT_MAX_MS after the wait
 * starts.
 *
 * Returns whether a landing candidate may open now. A demo that was there on
 * the first load of this workspace always may. One that appears later may only
 * open during the wait, and only if the user has not clicked or typed yet, so a
 * slow copy never pulls someone away from what they started.
 */
export function useSeededDemoWait(params: {
  workspaceId: string;
  isReady: boolean;
  hasLandingCandidate: boolean;
  shouldWait: boolean;
  refetch: () => unknown;
}): { landingAllowed: boolean } {
  const { workspaceId, isReady, hasLandingCandidate, shouldWait, refetch } = params;
  const [firstLoad, setFirstLoad] = useState<FirstLoad | null>(null);
  const [wait, setWait] = useState<Wait | null>(null);
  const refetchRef = useRef(refetch);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const firstLoadHere = firstLoad?.workspaceId === workspaceId ? firstLoad : null;
  const waitHere = wait?.workspaceId === workspaceId ? wait : null;
  const waitActive = Boolean(waitHere?.active);

  useEffect(() => {
    if (!isReady || firstLoadHere) return;
    setFirstLoad({ workspaceId, hadCandidate: hasLandingCandidate });
  }, [isReady, firstLoadHere, workspaceId, hasLandingCandidate]);

  useEffect(() => {
    if (!isReady || !shouldWait || waitHere) return;
    setWait({ workspaceId, active: true, interacted: false });
  }, [isReady, shouldWait, waitHere, workspaceId]);

  useEffect(() => {
    if (!waitActive) return;
    const update = (patch: Partial<Wait>) =>
      setWait((current) =>
        current?.workspaceId === workspaceId ? { ...current, ...patch } : current
      );
    const timer = window.setTimeout(() => update({ active: false }), DEMO_WAIT_MAX_MS);
    const onInteract = () => update({ interacted: true });
    window.addEventListener("pointerdown", onInteract, true);
    window.addEventListener("keydown", onInteract, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", onInteract, true);
      window.removeEventListener("keydown", onInteract, true);
    };
  }, [waitActive, workspaceId]);

  const polling = waitActive && shouldWait;
  useEffect(() => {
    if (!polling) return;
    const interval = window.setInterval(() => {
      void refetchRef.current();
    }, DEMO_WAIT_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [polling]);

  const landingAllowed =
    !firstLoadHere || firstLoadHere.hadCandidate || (waitActive && !waitHere?.interacted);
  return { landingAllowed };
}
